import { Button, Dialog, Field, NativeSelect, Portal, Text, Textarea, VStack } from '@chakra-ui/react'
import { Checkbox } from '@src/components/chakra-ui/checkbox'
import { attachmentCountLabel } from '@src/components/sco/Admin/AiCrm/crmAttachments'
import {
  commentsForGenerate,
  generateFileCount,
  type GenerateScope,
} from '@src/components/sco/Admin/AiCrm/crmComposerLogic'
import { ChoiceTiles } from '@src/components/sco/Admin/AiCrm/ui'
import type { ChatGenerateToolChoice } from '@src/components/sco/Chat/chatGenerate'
import React, { useEffect, useState } from 'react'

type GenerateComment = {
  body: string
  files?: Array<unknown> | null
  isDraft?: boolean
  withdrawnAt?: string | null
}

function snippet(body: string): string {
  const text = body.replace(/\s+/g, ' ').trim()
  if (!text) {
    return 'No text'
  }
  return text.length > 72 ? `${text.slice(0, 71)}…` : text
}

export type GenerateDraftRequest = {
  includeFiles: boolean
  instruction: string
  scope: GenerateScope
  /** Empty when this runs as its own AiAction on the account's reply tool. */
  toolId: string | null
}

/**
 * How Generate chooses context. The reply is written into the box. Nothing is sent.
 * Drafts and unsent messages are not offered. "This reply" is the account's reply tool.
 * Another chat tool adds that tool's prompt and knowledge.
 */
export function AiCrmGenerateDialog(props: {
  busy: boolean
  comments: GenerateComment[]
  needsConfirm: boolean
  onClose: () => void
  onGenerate: (input: GenerateDraftRequest) => Promise<string | null>
  open: boolean
  tools?: ChatGenerateToolChoice[]
}) {
  const tools = props.tools || []
  const [scope, setScope] = useState<GenerateScope>('last')
  const [includeFiles, setIncludeFiles] = useState(true)
  const [instruction, setInstruction] = useState('')
  const [viaToolId, setViaToolId] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!props.open) {
      setViaToolId('')
    }
  }, [props.open])
  const sent = commentsForGenerate(props.comments, 'thread')
  const last = sent[sent.length - 1]
  const fileCount = generateFileCount(props.comments, scope)
  const filesOn = fileCount > 0 && includeFiles

  function close() {
    if (props.busy) {
      return
    }
    setConfirming(false)
    props.onClose()
  }

  async function run() {
    setError(null)
    const message = await props.onGenerate({
      includeFiles: filesOn,
      instruction,
      scope,
      toolId: viaToolId || null,
    })
    setError(message)
    setConfirming(false)
  }

  return (
    <Dialog.Root
      open={props.open}
      onOpenChange={event => {
        if (!event.open) {
          close()
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{confirming ? 'Replace what is in the box?' : 'Generate a reply'}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              {confirming ? (
                <Text fontSize={'sm'}>
                  Generating a reply replaces what is in the box and any saved draft stays until you save this
                  one. Nothing is sent.
                </Text>
              ) : (
                <VStack align={'stretch'} gap={4}>
                  <Field.Root>
                    <Field.Label>Run through</Field.Label>
                    <NativeSelect.Root disabled={props.busy}>
                      <NativeSelect.Field
                        aria-label={'Run through'}
                        value={viaToolId}
                        onChange={event => setViaToolId(event.target.value)}
                      >
                        <option value={''}>This reply</option>
                        {tools.map(tool => (
                          <option key={tool.toolId} value={tool.toolId}>
                            {tool.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Text color={'fg.muted'} fontSize={'xs'}>
                      This reply uses this account's reply tool. A chat tool adds that tool's prompt and
                      knowledge.
                    </Text>
                  </Field.Root>
                  <ChoiceTiles<GenerateScope>
                    ariaLabel={'What to include'}
                    columns={2}
                    disabled={props.busy}
                    value={scope}
                    items={[
                      {
                        label: 'Last message',
                        hint: last ? snippet(last.body) : 'No message',
                        value: 'last',
                      },
                      {
                        label: 'This conversation',
                        hint: sent.length === 1 ? '1 message' : `${sent.length} messages`,
                        value: 'thread',
                      },
                    ]}
                    onChange={setScope}
                  />
                  <Checkbox
                    checked={filesOn}
                    disabled={props.busy || fileCount === 0}
                    size={'sm'}
                    onCheckedChange={event => setIncludeFiles(event.checked === true)}
                  >
                    {fileCount > 0
                      ? `Include ${attachmentCountLabel(fileCount)}`
                      : 'No files on these messages'}
                  </Checkbox>
                  <Field.Root>
                    <Field.Label>Additional instruction</Field.Label>
                    <Textarea
                      disabled={props.busy}
                      maxLength={4000}
                      placeholder={'Optional. For example: two sentences, and mention Thursday.'}
                      rows={3}
                      value={instruction}
                      onChange={event => setInstruction(event.target.value)}
                    />
                  </Field.Root>
                  {error ? (
                    <Text color={'fg.error'} fontSize={'sm'} role={'alert'}>
                      {error}
                    </Text>
                  ) : (
                    <Text color={'fg.muted'} fontSize={'xs'}>
                      Drafts and unsent messages stay out. Nothing is sent.
                    </Text>
                  )}
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              {confirming ? (
                <Button disabled={props.busy} variant={'outline'} onClick={() => setConfirming(false)}>
                  Back
                </Button>
              ) : (
                <Button disabled={props.busy} variant={'outline'} onClick={close}>
                  Cancel
                </Button>
              )}
              <Button
                colorPalette={'orange'}
                disabled={!confirming && sent.length === 0}
                loading={props.busy}
                onClick={() => {
                  if (!confirming && props.needsConfirm) {
                    setConfirming(true)
                    return
                  }
                  void run()
                }}
              >
                Generate
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
